import { locationApi } from "@api/locations";
import { withCancel } from "@api/utils";
import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { RJSForm } from "@forms/rjsf";
import { goTo } from "@history";
import { BackOfficeRoutes } from "@routes/urls";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import _cloneDeep from "lodash/cloneDeep";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Message, Modal, Header, Icon, Button } from "semantic-ui-react";
import { schema } from "./schema";
import { uiSchema } from "./uiSchema";
import { userApi } from "@api/users/user";
import { AuditService } from "@api/audit/audit";

export class LocationEditor extends Component {
  constructor(props) {
    super(props);

    const isEditing = !!props.match.params.locationPid;
    this.state = {
      data: {},
      isLoading: isEditing,
      error: {},
      validationErrors: [],
      showValidationModal: false,
    };
  }

  componentDidMount() {
    const { match } = this.props;
    if (match.params.locationPid) {
      this.fetch(match.params.locationPid);
    }
  }

  componentWillUnmount() {
    this.cancellableFetch && this.cancellableFetch.cancel();
  }

  fetch = async (locationPid) => {
    this.cancellableFetch = withCancel(locationApi.get(locationPid));
    try {
      const response = await this.cancellableFetch.promise;
      this.setState({ data: response.data, isLoading: false, error: {} });
    } catch (error) {
      if (error !== "UNMOUNTED") {
        this.setState({ isLoading: false, error: error });
      }
    }
  };

  prepareDataForCreation = () => {
    const defaultTimes = [
      { start_time: "08:00", end_time: "12:00" },
      { start_time: "13:00", end_time: "18:00" },
    ];
    return {
      opening_weekdays: [
        {
          weekday: "monday",
          is_open: true,
          times: defaultTimes,
        },
        {
          weekday: "tuesday",
          is_open: true,
          times: defaultTimes,
        },
        {
          weekday: "wednesday",
          is_open: true,
          times: defaultTimes,
        },
        {
          weekday: "thursday",
          is_open: true,
          times: defaultTimes,
        },
        {
          weekday: "friday",
          is_open: true,
          times: defaultTimes,
        },
        {
          weekday: "saturday",
          is_open: false,
        },
        {
          weekday: "sunday",
          is_open: false,
        },
      ],
      opening_exceptions: [],
    };
  };

  validateOpeningTimes = (data) => {
    const errors = [];

    if (data.opening_weekdays) {
      data.opening_weekdays.forEach((element) => {
        if (element.is_open) {
          const hasValidTimes =
            element.times &&
            element.times.length > 0 &&
            element.times.some(
              (timeSlot) =>
                timeSlot &&
                timeSlot.start_time &&
                timeSlot.end_time &&
                timeSlot.start_time.trim() !== "" &&
                timeSlot.end_time.trim() !== ""
            );

          if (!hasValidTimes) {
            const dayName =
              element.weekday.charAt(0).toUpperCase() +
              element.weekday.slice(1);
            errors.push(
              `${dayName} is marked as open but has no opening times set.`
            );
          }
        }
      });
    }

    return errors;
  };

  processData = (data) => {
    const processedData = _cloneDeep(data);

    if (processedData.opening_weekdays) {
      processedData.opening_weekdays.forEach((element) => {
        if (!element.is_open) {
          delete element.times;
        } else if (element.times) {
          element.times = element.times.filter((timeSlot) => {
            return (
              timeSlot &&
              timeSlot.start_time &&
              timeSlot.end_time &&
              timeSlot.start_time.trim() !== "" &&
              timeSlot.end_time.trim() !== ""
            );
          });

          if (element.times.length === 0) {
            delete element.times;
          }
        }
      });
    }

    if (!processedData.opening_exceptions) {
      processedData.opening_exceptions = [];
    } else {
      processedData.opening_exceptions.forEach((element) => {
        if (element.is_open === undefined) {
          element.is_open = false;
        }
      });
    }

    return processedData;
  };

  handleFormSubmit = async (formData) => {
    this.setState({ validationErrors: [], showValidationModal: false });

    const validationErrors = this.validateOpeningTimes(formData);
    if (validationErrors.length > 0) {
      this.setState({
        validationErrors,
        showValidationModal: true,
      });

      const error = new Error("Validation failed");
      error.skipFormError = true;
      throw error;
    }

    return this.submitAction(formData);
  };

  handleModalOk = () => {
    this.setState({
      showValidationModal: false,
    });
  };

  submitAction = async (formData) => {
    const {
      match: {
        params: { locationPid },
      },
    } = this.props;

    try {
      const processedFormData = this.processData(formData);

      const isEditing = !!locationPid;
      const response = isEditing
        ? await locationApi.update(locationPid, processedFormData)
        : await locationApi.create(processedFormData);

      // AUDIT LOG INSERTION
      try {
        const userResponse = await userApi.get();
        const user = userResponse?.data || userResponse;
        const userName = user.full_name || "Unknown User";
        const userRole = (user.roles && user.roles[0]) || "patron";
        const action = isEditing ? "Update Location" : "Create Location";
        const locationName =
          response?.data?.metadata?.name || "Unnamed Location";
        const locationPidResult =
          response?.data?.metadata?.pid || (locationPid ?? "Unknown PID");

        const details = `The user ${userName} (${userRole}) ${
          isEditing ? "updated" : "created"
        } the location "${locationName}" (PID: ${locationPidResult}) at ${new Date().toLocaleString()}.`;

        await AuditService.api.createLog({
          action,
          details,
          created_by: `${userName} (${userRole})`,
        });

        console.info(`Audit log for ${action} successful.`);
      } catch (auditError) {
        console.error(
          "Audit log for location creation/update failed:",
          auditError
        );
      }

      return response;
    } catch (error) {
      console.error("Error creating/updating location:", error);
      throw error;
    }
  };

  successCallback = (response) => {
    this.setState({ validationErrors: [], showValidationModal: false });
    goTo(BackOfficeRoutes.locationsDetailsFor(response.data.metadata.pid));
  };

  renderValidationModal = () => {
    const { showValidationModal, validationErrors } = this.state;

    return (
      <Modal
        open={showValidationModal}
        onClose={this.handleModalOk}
        size="small"
      >
        <Header>
          <Icon
            name="exclamation triangle"
            color="red"
            size="small"
            style={{ marginRight: "8px" }}
          />
          Validation Error
        </Header>
        <Modal.Content>
          <p>Please fix the following issues before submitting:</p>
          <Message negative>
            <Message.List>
              {validationErrors.map((error, index) => (
                <Message.Item key={index}>{error}</Message.Item>
              ))}
            </Message.List>
          </Message>
        </Modal.Content>
        <Modal.Actions>
          <Button color="green" onClick={this.handleModalOk}>
            <Icon name="checkmark" /> OK
          </Button>
        </Modal.Actions>
      </Modal>
    );
  };

  render() {
    const {
      match: {
        params: { locationPid },
      },
    } = this.props;
    const { isLoading, error, data, validationErrors } = this.state;

    const isEditing = !!locationPid;

    if (isEditing) {
      const formTitle = `Location - Edit #${locationPid}`;
      return (
        <>
          <Loader isLoading={isLoading}>
            <Error error={error}>
              <RJSForm
                schema={schema()}
                uiSchema={uiSchema(formTitle)}
                formData={data.metadata}
                submitAction={this.handleFormSubmit}
                successCallback={this.successCallback}
                successMessage="The location was successfully updated."
              />
            </Error>
          </Loader>
          {this.renderValidationModal()}
        </>
      );
    } else {
      const formTitle = "Location - Create";
      const prefilledFormData = _get(this.props, "location.state.formData", {});
      const formInitialData = !_isEmpty(prefilledFormData)
        ? prefilledFormData
        : this.prepareDataForCreation();

      return (
        <>
          {validationErrors.length > 0 && (
            <Message negative>
              <Message.Header>Please fix the following issues:</Message.Header>
              <Message.List>
                {validationErrors.map((error, index) => (
                  <Message.Item key={index}>{error}</Message.Item>
                ))}
              </Message.List>
            </Message>
          )}

          <RJSForm
            schema={schema()}
            uiSchema={uiSchema(formTitle)}
            formData={formInitialData}
            submitAction={this.handleFormSubmit}
            successCallback={this.successCallback}
            successMessage="The location was successfully created."
          />
          {this.renderValidationModal()}
        </>
      );
    }
  }
}

LocationEditor.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      locationPid: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      formData: PropTypes.object,
      extraData: PropTypes.object,
    }),
  }),
};

LocationEditor.defaultProps = {
  location: {
    state: null,
  },
};
